/**
 * Central, engine-independent configuration.
 * Every tunable number used by more than one module lives here so gameplay
 * values are never scattered across unrelated files.
 */

export interface MapConfig {
  /** Tiles along the grid X axis (rendered towards the lower-right). */
  readonly width: number;
  /** Tiles along the grid Y axis (rendered towards the lower-left). */
  readonly height: number;
  /** Seed for the deterministic terrain generator. Same seed = same map. */
  readonly seed: number;
  /** Radius (tiles) of guaranteed open, buildable ground around each start. */
  readonly startClearRadius: number;
}

export interface TileConfig {
  /** Diamond width in world pixels. 2x tileHeight (classic 2:1) is recommended; see runtimeConfig limits. */
  readonly width: number;
  /** Diamond height in world pixels. */
  readonly height: number;
}

export interface CameraConfig {
  readonly minZoom: number;
  readonly maxZoom: number;
  readonly defaultZoom: number;
  /** Multiplicative zoom factor per wheel notch / zoom button press. */
  readonly zoomStep: number;
  /** Keyboard pan speed in screen pixels per second (independent of zoom). */
  readonly keyboardPanSpeed: number;
  /**
   * How far (in tiles) the view centre may move past the map edge. Measured in
   * tiles so the boundary behaves the same for any tile size.
   */
  readonly boundsPaddingTiles: number;
  /**
   * Smooth-zoom speed (1/s). Higher = snappier. 0 disables smoothing
   * (zoom jumps instantly).
   */
  readonly zoomSmoothing: number;
  /** Zoom factor per HUD zoom-button press. */
  readonly buttonZoomStep: number;
  /** Start / reset zoom on touch-first devices (bigger tiles are easier to tap). */
  readonly touchDefaultZoom: number;
}

export interface InputConfig {
  /** Pointer travel (screen px) after which a press becomes a drag instead of a click. */
  readonly dragThreshold: number;
  /** Same for touch: fingers wobble more than a mouse, so a tap tolerates more travel. */
  readonly touchDragThreshold: number;
}

export interface SimulationConfig {
  /** Fixed simulation ticks per second, independent of render frame rate. */
  readonly ticksPerSecond: number;
  /** Upper bound on ticks processed per frame to avoid a spiral of death after a stall. */
  readonly maxTicksPerFrame: number;
  /** Game-speed multiplier (1 = real time). Testing / sandbox only (`?speed=`). */
  readonly speed: number;
}

export interface RenderConfig {
  /** Tiles per side of a culling chunk. Only chunks near the view are drawn. */
  readonly chunkSize: number;
}

export interface ColonyConfig {
  /** Ownership rule: players may build within this many tiles of their own buildings. */
  readonly buildRadius: number;
  /** Players in the local sandbox match (1–4). Player 1 is local; others have colonies but no controller yet. */
  readonly players: number;
  /** Starting resources for every player; undefined = ECONOMY.startingResources. Test/sandbox override (`?resources=`). */
  readonly startingResources?: Readonly<Partial<Record<'wood' | 'scrap' | 'food', number>>>;
  /** Starting survivors for every player; undefined = POPULATION.startingSurvivors. Test/sandbox override (`?survivors=`). */
  readonly startingSurvivors?: number;
  /** Test / sandbox only: Rangers given to player 1 at the start (`?units=`). */
  readonly startingUnits?: number | Readonly<Partial<Record<'ranger' | 'heavy' | 'engineer', number>>>;
  /** Test / sandbox: enemy squad (second player) placed near the first player's base (`?enemies=`). */
  readonly startingEnemies?: number;
  /** Walkers in the infected zone at the start (`?zombies=`; default HORDE.initial). */
  readonly startingInfected?: number;
  /** Periodic hordes (`?waves=0` turns them off). */
  readonly waves?: boolean;
  /** Game mode (`?mode=sandbox|classic|last_colony|raid`), Phase 9. */
  readonly mode?: 'sandbox' | 'classic' | 'last_colony' | 'raid' | 'duel';
  /** Campaign level (`?level=N`), D109. */
  readonly level?: number;
  /** Weekly Challenge (`?weekly=2026-W45`), D123. */
  readonly weekly?: string;
}

export interface GameConfig {
  readonly title: string;
  readonly map: MapConfig;
  readonly tile: TileConfig;
  readonly camera: CameraConfig;
  readonly input: InputConfig;
  readonly simulation: SimulationConfig;
  readonly render: RenderConfig;
  readonly colony: ColonyConfig;
}

export const GAME_CONFIG: GameConfig = {
  title: 'After the Fall',
  map: {
    width: 64,
    height: 64,
    seed: 1066,
    startClearRadius: 6,
  },
  tile: {
    width: 64,
    height: 32,
  },
  camera: {
    minZoom: 0.35,
    maxZoom: 2.5,
    defaultZoom: 1,
    zoomStep: 1.15,
    keyboardPanSpeed: 900,
    boundsPaddingTiles: 5,
    zoomSmoothing: 14,
    buttonZoomStep: 1.25,
    touchDefaultZoom: 1.5,
  },
  input: {
    dragThreshold: 6,
    touchDragThreshold: 10,
  },
  simulation: {
    ticksPerSecond: 10,
    maxTicksPerFrame: 5,
    speed: 1,
  },
  render: {
    chunkSize: 8,
  },
  colony: {
    buildRadius: 10,
    players: 2,
  },
};
